/**
 * OnboardingTour — first-login guided tour (GH issue #4).
 *
 * Mounted once inside AppShell. On the first login of a user it walks
 * them through the main UI areas with a spotlight overlay + popover.
 *
 * Behaviour:
 *   - Auto-starts only when the user has never completed/dismissed it.
 *     Source of truth is users.has_seen_tour in Supabase, with a
 *     per-user localStorage flag as fallback (covers the window before
 *     the DB write lands, and databases without the column).
 *   - Steps are role-aware (built from RoleAwareNav data).
 *   - Dismissible at any step (Skip button / Escape). Completion AND
 *     dismissal both persist, so the tour never re-triggers.
 *   - Targets are looked up by [data-tour] attribute; when a target is
 *     not visible (mobile drawer closed, sidebar hidden) the step falls
 *     back to its mobileTarget or to a centred card, so the tour works
 *     on phones and tablets too.
 *   - Steps can live on specific pages (step.segment): the tour navigates
 *     there mid-walk to spotlight real workflows (create task, work the
 *     task table, answer doubts). Route changes remount AppShell, so the
 *     current step index lives in sessionStorage and the tour resumes on
 *     the new page — the same mechanism survives in-tour reloads.
 *
 * Positioning is IMPERATIVE on purpose: the spotlight/popover geometry
 * is written straight to the DOM nodes inside one layout effect, and the
 * only layout-derived React state is the `hasTarget` boolean. Geometry
 * objects in state previously caused an infinite update loop: a re-measure
 * dispatched from a timeout got rebased across the nested sync commits of
 * a layout effect, its updater re-ran each pass and minted a fresh rect
 * object, which re-triggered the placement effect that depended on the
 * rect's identity — "Maximum update depth exceeded" and a blank screen.
 */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { getRolePrefix } from '../../utils/roleUtils.js';
import {
  getStoredSidebarMode,
  setStoredSidebarMode,
} from '../../hooks/useSidebarMode.js';
import { buildTourSteps, TOUR_START_EVENT } from './tourSteps.js';

const seenKey = (uid) => `orin-tour-seen:${uid}`;
const stepKey = (uid) => `orin-tour-step:${uid}`;

function hasLocalSeen(uid) {
  try {
    return localStorage.getItem(seenKey(uid)) === '1';
  } catch {
    return false;
  }
}

function isVisible(el) {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** First *visible* element for the step's target (then mobileTarget). */
function resolveTargetEl(step) {
  for (const key of [step.target, step.mobileTarget]) {
    if (!key) continue;
    const els = document.querySelectorAll(`[data-tour="${key}"]`);
    for (const el of els) {
      if (isVisible(el)) return el;
    }
  }
  return null;
}

const SPOT_PAD = 6; // px of breathing room around the highlighted element

/**
 * The font-size setting zooms <body> (applySettings.js). Zoom scales the
 * rendered position of the portal's fixed elements, while measured client
 * rects stay in visual viewport px — so specified coordinates must be
 * divided by the zoom factor to land where the measurement says.
 */
function bodyZoom() {
  const z = parseFloat(window.getComputedStyle(document.body).zoom);
  return Number.isFinite(z) && z > 0 ? z : 1;
}

export default function OnboardingTour() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = profile?.role ?? null;
  const steps = useMemo(() => (role ? buildTourSteps(role) : []), [role]);

  const [stepIndex, setStepIndex] = useState(null); // null = tour inactive
  const [hasTarget, setHasTarget] = useState(false); // spotlight vs centred dim

  const popRef = useRef(null);
  const spotRef = useRef(null);
  const targetElRef = useRef(null);
  const stepRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const autoStartedRef = useRef(false);
  const titleId = useId();
  const bodyId = useId();

  const active = stepIndex !== null && steps.length > 0;

  /* ── Imperative positioning ───────────────────────────────── */

  /**
   * Measures the current target and writes spotlight + popover geometry
   * straight to the DOM. The only setState here is the `hasTarget`
   * boolean, which React bails out on when unchanged — this function is
   * safe to call from effects, timeouts and scroll/resize listeners.
   */
  const position = useCallback(() => {
    const pop = popRef.current;
    const step = stepRef.current;
    if (!pop || !step) return;

    const el = targetElRef.current;
    const visible = !!(el && el.isConnected && isVisible(el));
    setHasTarget(visible);

    const zoom = bodyZoom();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // offset* are layout px; convert to visual px to mix with rect/viewport
    const pw = pop.offsetWidth * zoom;
    const ph = pop.offsetHeight * zoom;
    const margin = 12;
    const gap = 14;

    let top;
    let left;

    if (!visible) {
      top = Math.max(margin, (vh - ph) / 2);
      left = Math.max(margin, (vw - pw) / 2);
    } else {
      const r = el.getBoundingClientRect();

      const spot = spotRef.current;
      if (spot) {
        spot.style.top = `${(r.top - SPOT_PAD) / zoom}px`;
        spot.style.left = `${(r.left - SPOT_PAD) / zoom}px`;
        spot.style.width = `${(r.width + SPOT_PAD * 2) / zoom}px`;
        spot.style.height = `${(r.height + SPOT_PAD * 2) / zoom}px`;
      }

      const preferred = step.placement || 'right';
      const fits = {
        right: r.left + r.width + gap + pw <= vw - margin,
        left: r.left - gap - pw >= margin,
        bottom: r.top + r.height + gap + ph <= vh - margin,
        top: r.top - gap - ph >= margin,
      };
      const tryOrder = {
        right: ['right', 'left', 'bottom', 'top'],
        left: ['left', 'right', 'bottom', 'top'],
        bottom: ['bottom', 'top', 'right', 'left'],
        top: ['top', 'bottom', 'right', 'left'],
      }[preferred];
      const side = tryOrder.find((s) => fits[s]) || 'bottom';

      if (side === 'right') {
        left = r.left + r.width + gap;
        top = r.top;
      } else if (side === 'left') {
        left = r.left - gap - pw;
        top = r.top;
      } else if (side === 'bottom') {
        left = r.left;
        top = r.top + r.height + gap;
      } else {
        left = r.left;
        top = r.top - gap - ph;
      }
      left = Math.min(Math.max(left, margin), vw - pw - margin);
      top = Math.min(Math.max(top, margin), vh - ph - margin);
    }

    // Back to layout px so the zoomed render lands on the visual position
    pop.style.top = `${top / zoom}px`;
    pop.style.left = `${left / zoom}px`;
    pop.style.visibility = 'visible';
  }, []);

  /* ── Start / stop ─────────────────────────────────────────── */

  const start = useCallback(
    (at = 0) => {
      if (!steps.length) return;
      // The tour points at the rail — make sure it's on screen (desktop)
      if (window.innerWidth >= 1024 && getStoredSidebarMode() === 'closed') {
        setStoredSidebarMode('open');
      }
      restoreFocusRef.current = document.activeElement;
      setHasTarget(false);
      setStepIndex(Math.min(Math.max(at, 0), steps.length - 1));
    },
    [steps.length],
  );

  const markSeen = useCallback(() => {
    const uid = user?.id;
    if (!uid) return;
    try {
      localStorage.setItem(seenKey(uid), '1');
      sessionStorage.removeItem(stepKey(uid));
    } catch {
      /* storage blocked — DB flag still persists below */
    }
    // Allowed by the "Users can update own profile" RLS policy. If the
    // column doesn't exist yet the localStorage flag above still holds.
    if (profile?.has_seen_tour !== true) {
      supabase
        .from('users')
        .update({ has_seen_tour: true })
        .eq('id', uid)
        .then(({ error }) => {
          if (error) {
            console.warn('[ORIN] Could not persist tour completion:', error.message);
          }
        });
    }
  }, [user?.id, profile?.has_seen_tour]);

  const dismiss = useCallback(() => {
    markSeen();
    setStepIndex(null);
    setHasTarget(false);
    const prev = restoreFocusRef.current;
    if (prev && typeof prev.focus === 'function' && prev.isConnected) {
      prev.focus({ preventScroll: true });
    }
  }, [markSeen]);

  const next = useCallback(() => {
    if (stepIndex === null) return;
    if (stepIndex >= steps.length - 1) {
      dismiss(); // finishing the last step also persists completion
    } else {
      setStepIndex(stepIndex + 1);
    }
  }, [stepIndex, steps.length, dismiss]);

  const back = useCallback(() => {
    setStepIndex((i) => (i === null ? i : Math.max(0, i - 1)));
  }, []);

  /* ── Auto-start on first login ────────────────────────────── */

  useEffect(() => {
    if (autoStartedRef.current || active) return undefined;
    if (!user?.id || !profile) return undefined;

    let savedStep = null;
    try {
      savedStep = sessionStorage.getItem(stepKey(user.id));
    } catch {
      /* storage blocked — treat as no tour in progress */
    }
    const resuming = savedStep !== null;

    // A tour in progress (the tour navigates between pages mid-walk, which
    // remounts AppShell) must resume even for users already marked seen —
    // that's what carries a replay across page hops. Otherwise the DB flag
    // wins; undefined (column not deployed yet) defers to localStorage.
    if (!resuming && (profile.has_seen_tour === true || hasLocalSeen(user.id))) {
      return undefined;
    }

    autoStartedRef.current = true;
    const at = resuming ? parseInt(savedStep, 10) || 0 : 0;
    // Fresh starts wait for the shell to settle; page-hop resumes are quick
    const t = setTimeout(() => start(at), resuming ? 250 : 600);
    return () => clearTimeout(t);
  }, [user?.id, profile, active, start]);

  /* Manual replay (Settings → "Replay tour") */
  useEffect(() => {
    const onReplay = () => start(0);
    window.addEventListener(TOUR_START_EVENT, onReplay);
    return () => window.removeEventListener(TOUR_START_EVENT, onReplay);
  }, [start]);

  /* Remember progress so a mid-tour reload resumes where it left off */
  useEffect(() => {
    if (!active || !user?.id) return;
    try {
      sessionStorage.setItem(stepKey(user.id), String(stepIndex));
    } catch {
      /* best-effort */
    }
  }, [active, stepIndex, user?.id]);

  /* ── Per-step target resolution + placement ───────────────── */

  useLayoutEffect(() => {
    if (!active) return undefined;
    const step = steps[stepIndex];
    stepRef.current = step;

    // Page-specific step? Go to its page first — the route change remounts
    // AppShell (and this component), and the auto-start effect resumes the
    // tour at this step from sessionStorage on the new page.
    if (step.segment && role) {
      const expected = `${getRolePrefix(role)}/${step.segment}`;
      if (location.pathname !== expected) {
        if (popRef.current) popRef.current.style.visibility = 'hidden';
        navigate(expected);
        return undefined;
      }
    }

    const el = resolveTargetEl(step);
    targetElRef.current = el;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    position();
    // Re-measure after the sidebar width / drawer transitions settle
    const t1 = setTimeout(position, 140);
    const t2 = setTimeout(position, 320);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [active, stepIndex, steps, role, location.pathname, navigate, position]);

  /* The spotlight node (re)mounts when hasTarget flips — give it its
     geometry within the same commit so it never paints unpositioned. */
  useLayoutEffect(() => {
    if (active) position();
  }, [active, hasTarget, position]);

  /* Track the target through scrolling and window resizes */
  useEffect(() => {
    if (!active) return undefined;
    let raf = 0;
    const queue = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(position);
    };
    window.addEventListener('resize', queue);
    window.addEventListener('scroll', queue, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', queue);
      window.removeEventListener('scroll', queue, true);
    };
  }, [active, position]);

  /* ── Keyboard & focus ─────────────────────────────────────── */

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, dismiss, next, back]);

  useEffect(() => {
    if (!active) return;
    const pop = popRef.current;
    if (pop && !pop.contains(document.activeElement)) {
      pop.focus({ preventScroll: true });
    }
  }, [active, stepIndex]);

  const trapTab = (e) => {
    if (e.key !== 'Tab') return;
    const buttons = popRef.current?.querySelectorAll('button');
    if (!buttons?.length) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === popRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* ── Render ───────────────────────────────────────────────── */

  if (!active) return null;

  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  return createPortal(
    <>
      {/* Click shield — keeps the page inert while touring */}
      <div className="orin-tour-shield" aria-hidden="true" />

      {/* Distinct keys: the spotlight carries imperative inline geometry
          that must never leak onto the full-screen dim via fiber reuse. */}
      {hasTarget ? (
        <div key="spotlight" ref={spotRef} className="orin-tour-spotlight" aria-hidden="true" />
      ) : (
        <div key="dim" className="orin-tour-dim orin-anim-fade" aria-hidden="true" />
      )}

      <div
        ref={popRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        onKeyDown={trapTab}
        className="orin-tour-pop orin-anim-pop"
        style={{ top: 0, left: 0, visibility: 'hidden' }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted">
            {stepIndex + 1} of {steps.length}
          </span>
          <button
            type="button"
            onClick={dismiss}
            className="text-xs font-medium text-muted hover:text-ink transition-colors"
          >
            Skip tour
          </button>
        </div>

        <h2 id={titleId} className="text-[15px] font-bold text-ink mb-1">
          {step.title}
        </h2>
        <p id={bodyId} className="text-sm text-muted leading-relaxed mb-4">
          {step.body}
        </p>

        <div className="flex items-center gap-1.5">
          {steps.map((s, i) => (
            <span
              key={s.id}
              aria-hidden="true"
              className="w-1.5 h-1.5 rounded-full transition-colors"
              style={{ background: i === stepIndex ? 'var(--accent)' : 'var(--border)' }}
            />
          ))}
          <div className="ml-auto flex items-center gap-2">
            {!isFirst && (
              <button type="button" className="btn text-xs px-2.5 py-1.5" onClick={back}>
                Back
              </button>
            )}
            <button
              type="button"
              className="btn-primary text-xs px-3 py-1.5"
              onClick={isLast ? dismiss : next}
            >
              {isFirst ? 'Start tour' : isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
